import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import * as OneSignalService from '@/lib/oneSignalService';
import { toast } from 'sonner';
import { NotificationOnboardingScreen } from './NotificationOnboardingScreen';

const PROMPT_SESSION_KEY = 'notif_prompt_done';
export const TRIGGER_ONBOARDING_EVENT = 'trigger-notification-onboarding';

export function NotificationPermissionModal() {
  const { user, profile, updateProfile } = useAuth();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const hasChecked = useRef(false);

  // Listen for manual trigger event
  useEffect(() => {
    const handleTrigger = () => {
      setShowIOSGuide(OneSignalService.isIOS() && !OneSignalService.isInstalledPWA());
      setShowOnboarding(true);
    };
    
    window.addEventListener(TRIGGER_ONBOARDING_EVENT, handleTrigger);
    return () => window.removeEventListener(TRIGGER_ONBOARDING_EVENT, handleTrigger);
  }, []);

  useEffect(() => {
    if (!user || hasChecked.current) return;
    hasChecked.current = true;

    const checkNotifications = async () => {
      const permission = OneSignalService.getPermission();
      
      // Already granted - just ensure subscription is saved
      if (permission === 'granted') {
        if (!profile?.push_notifications_enabled) {
          const result = await OneSignalService.subscribe(user.id);
          if (result.success) {
            await updateProfile({ push_notifications_enabled: true });
          }
        }
        return;
      }

      // Denied - can't do anything
      if (permission === 'denied') return;

      // Already prompted this session
      if (sessionStorage.getItem(PROMPT_SESSION_KEY)) return;

      // Check platform requirements
      const ios = OneSignalService.isIOS();
      const supported = OneSignalService.isPushSupported();
      
      if (!supported) return;
      
      if (ios) {
        if (!OneSignalService.isInstalledPWA()) {
          setShowIOSGuide(true);
          setTimeout(() => setShowOnboarding(true), 7000);
          return;
        }
      }

      // Show onboarding after short delay (after PWA prompt)
      setTimeout(() => setShowOnboarding(true), 7000);
    };

    checkNotifications();
  }, [user, profile, updateProfile]);

  const handleRequestPermission = useCallback(async (): Promise<boolean> => {
    if (!user) return false;

    try {
      const result = await OneSignalService.subscribe(user.id);
      
      if (result.success) {
        await updateProfile({ push_notifications_enabled: true });
        toast.success('Notifications activées !');
        sessionStorage.setItem(PROMPT_SESSION_KEY, '1');
        return true;
      } else {
        // Check if denied or just dismissed
        const newPermission = OneSignalService.getPermission();
        if (newPermission === 'denied') {
          sessionStorage.setItem(PROMPT_SESSION_KEY, '1');
        }
        return false;
      }
    } catch (error) {
      console.error('[NotificationModal] Error:', error);
      return false;
    }
  }, [user, updateProfile]);

  const handleComplete = useCallback(() => {
    setShowOnboarding(false);
    sessionStorage.setItem(PROMPT_SESSION_KEY, '1');
  }, []);

  const handleSkip = useCallback(() => {
    setShowOnboarding(false);
    sessionStorage.setItem(PROMPT_SESSION_KEY, '1');
  }, []);

  if (!user || !showOnboarding) return null;

  return (
    <NotificationOnboardingScreen
      onComplete={handleComplete}
      onSkip={handleSkip}
      onRequestPermission={handleRequestPermission}
      showIOSGuide={showIOSGuide}
    />
  );
}
